<template>
    <div class="enquiry-centre-webview-content max-z-index">
        <div class="iframe-loading" v-loading="isLoading" v-if="isLoading"></div>
        <div v-if="isWebview && !!enquiryCentreUrl" class="enquiry-centre-webview-header">
            <el-button size="small" @click="goBackToEnquiry">
                <img class="enquiry-centre-webview-back-icon" :src="enquiryIcon" alt=""/>
            </el-button>
        </div>
        <iframe
            v-show="!!enquiryCentreUrl"
            :key="iframeKey"
            width="100%"
            @load="onIframeLoad"
            :src="enquiryCentreUrl"
            frameborder="0"
            ref="enquiryCentreIframe"
            id="enquiryCentreIframe"
            class="enquiry-centre-webview-iframe"
            allow="clipboard-read; clipboard-write"
        ></iframe>
        <agenda-confirmation v-if="visibleConfirmAgenda" :visible.sync="visibleConfirmAgenda" :agenda-id="confirmAgendaId" embedded dialog/>
        <agenda-add-comment v-if="visibleAddComment" :visible.sync="visibleAddComment" :event="addCommentEvent" :is-on-drawer="true" :drawer-context="pendingCommentContext"/>
        <agenda-add-person v-if="visibleAddPerson" :visible.sync="visibleAddPerson" :child="addPersonChild"
            :is-on-drawer="true" :drawer-context="pendingAddPersonContext" @handle-create="onAddPersonCreateNew"/>
        <email-out
            :visible.sync="visibleEmailOut"
            :history="selectedEmailOut"
            :module-type="emailOutModuleType"
        />
    </div>
</template>
<script>
import PopupManager from "element-ui/lib/utils/popup/popup-manager";
import { EVENT_BUS, WEBAPP_LAYOUT } from "../../../common/constants";
import { DRAWER_NAME } from "../../../common/drawer-component.constants";
import { WINAPP_METHOD } from "../../../common/win-app-method.constants";
import moduleConstant from "../../../common/module-constant";
import { DrawerModel } from "../../../models/common/drawer.model";
import { storeModules } from "../../../store";
import { ENQUIRY_CENTRE_ORIGIN, EXTERNAL_APP_URI } from "../../../common/uri/property-uri.constants";
import { MODULE_ICON_URLS } from "../../../common/uri/module-icons.constants";
import { embeddedAppTokenBroker } from "../../../utils/embedded-app-token";
import { LanguageHelper } from "../../../utils/language-helper";
import { postMessageForWebview } from "../../../utils/web-view";
import { AgendaServices, TaskServices } from "../../../services";
import EventBus from "../../../utils/event-bus";
import { resolveCreateTaskDrawer } from "../create-task-handoff";
import {
    ENQUIRY_EVENT,
    ENQUIRY_RESPONSE,
    ENQUIRY_TASK_CONTEXT_PREFIX,
    ENQUIRY_APPOINTMENT_CONTEXT_PREFIX,
    ENQUIRY_EDIT_APPOINTMENT_CONTEXT_PREFIX,
} from "../enquiry-events.constants";

// One above `.max-z-index` (99999) so the drawer stacks over the full-screen webview iframe.
const WEBVIEW_DRAWER_Z_INDEX = 100000;

const agendaService = new AgendaServices();
const taskService = new TaskServices();

export default {
    name: 'EnquiryCentreWebview',
    components: {
        'agenda-confirmation': () => import('../../agenda/components/agenda-confirmation-component/view/web-app/AgendaConfirmationComponent.vue'),
        'agenda-add-comment': () => import('../../agenda/components/agenda-add-comment-component/AgendaAddCommentComponent.vue'),
        'agenda-add-person': () => import('../../agenda/components/agenda-add-person-component/AgendaAddPersonComponent.vue'),
        'email-out': () => import('../../history/email-out/EmailOutComponent.vue'),
    },
    data() {
        return {
            enquiryCentreUrl: '',
            // Bumped on "back" to force the iframe to remount and reload the Enquiry home,
            // since in-iframe navigation leaves `enquiryCentreUrl` unchanged.
            iframeKey: 0,
            // GROUPED is the near-black (#303133) enquiry glyph; NORMAL/ACTIVE are light/blue.
            enquiryIcon: MODULE_ICON_URLS['enquiry-task'].GROUPED,
            isLoading: true,
            pendingTaskContext: null,
            pendingAppointmentContext: null,
            pendingEditAppointmentContext: null,
            pendingCommentContext: null,
            pendingAddPersonContext: null,
            visibleConfirmAgenda: false,
            confirmAgendaId: null,
            visibleEmailOut: false,
            selectedEmailOut: null,
            visibleAddComment: false,
            addCommentEvent: null,
            visibleAddPerson: false,
            addPersonChild: null,
            // Enquiry email histories are person histories; the forward-mail payload and
            // history-detail drawer only understand Object/Person/Demand modules.
            emailOutModuleType: moduleConstant.Person,
        };
    },
    computed: {
        webappLayoutMode() {
            return this.$store.state[storeModules.Common].webappLayoutMode;
        },
        isWebview() {
            return this.webappLayoutMode === WEBAPP_LAYOUT.WEBVIEW;
        },
    },
    methods: {
        onIframeLoad() {
            this.isLoading = false;
        },
        buildEnquiryCentreUrl() {
            return `${EXTERNAL_APP_URI.ENQUIRY_CENTRE_APP}?language=${window['webViewLang'] || ''}`;
        },
        goBackToEnquiry() {
            this.isLoading = true;
            this.enquiryCentreUrl = this.buildEnquiryCentreUrl();
            // Remount the iframe so it reloads the Enquiry home even when the URL is unchanged.
            this.iframeKey += 1;
        },
        postMessageToIframe(type) {
            const iframe = this.$refs.enquiryCentreIframe;
            if (iframe && iframe.contentWindow) {
                iframe.contentWindow.postMessage({ type }, ENQUIRY_CENTRE_ORIGIN);
            }
        },
        openTaskDrawer(props, title) {
            this.pendingTaskContext = `${ENQUIRY_TASK_CONTEXT_PREFIX}-${Date.now()}`;
            this.$store.commit(`${storeModules.Common}/setVisibleDrawerModel`, new DrawerModel({
                componentName: DRAWER_NAME.TASK_NEW_FORM,
                context: this.pendingTaskContext,
                props,
                title,
                showSaveBtn: true,
                // Must exceed the iframe wrapper's `max-z-index` (99999) so the drawer renders above the full-screen webview iframe.
                zIndex: WEBVIEW_DRAWER_Z_INDEX,
            }));
        },
        // Same drawer flow as the WebApp SPA host: prefill with the group's own
        // user, property and slot only â€” no task/person prefill, like the Add to
        // group action on the /agenda calendar.
        openAddToGroupAppointmentDrawer(payload) {
            this.pendingAppointmentContext = `${ENQUIRY_APPOINTMENT_CONTEXT_PREFIX}-${Date.now()}`;
            this.$store.commit(`${storeModules.Common}/setVisibleDrawerModel`, new DrawerModel({
                componentName: DRAWER_NAME.APPOINTMENT_NEW_FORM,
                context: this.pendingAppointmentContext,
                props: {
                    userId: payload.userId,
                    startTime: payload.startTime,
                    propertyId: payload.propertyId,
                },
                title: 'NEW_APPOINTMENT',
                showSaveBtn: true,
                zIndex: WEBVIEW_DRAWER_Z_INDEX,
            }));
        },
        openPersonDetailDrawer(personId) {
            this.$store.commit(`${storeModules.Common}/setVisibleDrawerModel`, new DrawerModel({
                componentName: DRAWER_NAME.PERSON_DETAIL,
                props: { objectId: personId },
                title: 'PERSON',
                zIndex: WEBVIEW_DRAWER_Z_INDEX,
            }));
        },
        openPropertyDetailDrawer(propertyId) {
            this.$store.commit(`${storeModules.Common}/setVisibleDrawerModel`, new DrawerModel({
                componentName: DRAWER_NAME.PROPERTY_DETAIL,
                props: { objectId: propertyId },
                title: 'PROPERTY',
                zIndex: WEBVIEW_DRAWER_Z_INDEX,
            }));
        },
        showAppointmentConfirmation(appointmentId) {
            this.confirmAgendaId = appointmentId.toString();
            // The webview iframe is fixed at a high z-index; lift Element's popup counter so the
            // confirmation dialog (append-to-body) stacks above it, matching the drawer handling.
            if (PopupManager.zIndex <= WEBVIEW_DRAWER_Z_INDEX) {
                PopupManager.zIndex = WEBVIEW_DRAWER_Z_INDEX + 1;
            }
            this.visibleConfirmAgenda = true;
        },
        openEmailOut(historyRootId) {
            // Same z-index handling as showAppointmentConfirmation: the email-out dialog is append-to-body.
            if (PopupManager.zIndex <= WEBVIEW_DRAWER_Z_INDEX) {
                PopupManager.zIndex = WEBVIEW_DRAWER_Z_INDEX + 1;
            }
            this.selectedEmailOut = { RootId: historyRootId };
            this.visibleEmailOut = true;
        },
        onTaskCreatedFromEnquiry(payload) {
            if (!payload || !payload.context || payload.context !== this.pendingTaskContext) return;
            this.pendingTaskContext = null;
            this.postMessageToIframe(ENQUIRY_RESPONSE.TASK_CREATED);
        },
        // The appointment form emits the same saved event for creates, edits and
        // the comment dialog; the context tells them apart so the Enquiry app gets
        // the right ack.
        onAppointmentCreatedFromEnquiry(payload) {
            if (!payload || !payload.context) return;
            if (payload.context === this.pendingAppointmentContext) {
                this.pendingAppointmentContext = null;
                this.postMessageToIframe(ENQUIRY_RESPONSE.APPOINTMENT_CREATED);
            } else if (payload.context === this.pendingEditAppointmentContext) {
                this.pendingEditAppointmentContext = null;
                this.postMessageToIframe(ENQUIRY_RESPONSE.APPOINTMENT_UPDATED);
            } else if (payload.context === this.pendingCommentContext) {
                this.pendingCommentContext = null;
                this.postMessageToIframe(ENQUIRY_RESPONSE.APPOINTMENT_UPDATED);
            } else if (payload.context === this.pendingAddPersonContext) {
                this.pendingAddPersonContext = null;
                this.postMessageToIframe(ENQUIRY_RESPONSE.APPOINTMENT_UPDATED);
            }
        },
        // The add-person dialog's "New appointment" shortcut: the dialog sends
        // the group context it resolved itself (calendar user, property, first
        // free time after the group) â€” reuse the add-to-group drawer.
        onAddPersonCreateNew(context) {
            if (!context || !context.userId || !context.startTime || !context.propertyId) return;
            this.openAddToGroupAppointmentDrawer(context);
        },
        openEditAppointmentDrawer(appointmentId) {
            this.pendingEditAppointmentContext = `${ENQUIRY_EDIT_APPOINTMENT_CONTEXT_PREFIX}-${Date.now()}`;
            this.$store.commit(`${storeModules.Common}/setVisibleDrawerModel`, new DrawerModel({
                componentName: DRAWER_NAME.APPOINTMENT_EDIT_FORM,
                context: this.pendingEditAppointmentContext,
                props: { objectId: appointmentId },
                title: 'EDIT_APPOINTMENT',
                showSaveBtn: true,
                zIndex: WEBVIEW_DRAWER_Z_INDEX,
            }));
        },
        // Element popups (dialogs, confirm boxes) must stack above the full-screen
        // webview iframe â€” same counter lift as showAppointmentConfirmation.
        liftPopupZIndex() {
            if (PopupManager.zIndex <= WEBVIEW_DRAWER_Z_INDEX) {
                PopupManager.zIndex = WEBVIEW_DRAWER_Z_INDEX + 1;
            }
        },
        // The comment dialog loads the appointment itself from {Id, StartTime, UserId}.
        // PropertyId and Person.Id must be real ids: the dialog resolves the comment
        // slot by matching them against the loaded appointment's property/person
        // lists. The names only feed its read-only header labels.
        openAddCommentDialog(payload) {
            this.liftPopupZIndex();
            this.pendingCommentContext = `${ENQUIRY_EDIT_APPOINTMENT_CONTEXT_PREFIX}-comment-${Date.now()}`;
            this.addCommentEvent = {
                Id: payload.appointmentId,
                StartTime: payload.startTime,
                UserId: payload.userId,
                PropertyId: payload.propertyId,
                AppointmentTypeId: payload.appointmentTypeId || null,
                Person: payload.personId ? { Id: payload.personId, Name: payload.personName } : null,
                superPropertyName: payload.propertyName || '',
            };
            this.visibleAddComment = true;
        },
        openCreateDemandDrawer(payload) {
            this.$store.commit(`${storeModules.Common}/setVisibleDrawerModel`, new DrawerModel({
                componentName: DRAWER_NAME.DEMAND_NEW_FORM,
                props: { data: { PropertyId: payload.propertyId, PersonId: payload.personId } },
                title: 'NEW_DEMAND',
                showSaveBtn: true,
                zIndex: WEBVIEW_DRAWER_Z_INDEX,
            }));
        },
        deleteAppointment(appointmentId) {
            this.liftPopupZIndex();
            // Not every locale's string carries the "|title" part (e.g. de-BE)
            const [message, title] = this.$t('DELETE_APPOINTMENT_CONFIRM').toString().split('|');
            this.$confirm(message, title || this.$t('WARNING').toString(), { type: 'warning' })
                // The Enquiry popover only lists simple (non-recurring) appointments
                .then(() => agendaService.deleteAppointment(
                    { originId: appointmentId, masterId: appointmentId, isRecurrence: false, isException: false },
                    null, null, true))
                .then(() => this.postMessageToIframe(ENQUIRY_RESPONSE.APPOINTMENT_DELETED))
                .catch((error) => {
                    if (error !== 'cancel' && error !== 'close') this.$notify.error(error);
                });
        },
        // The add-person dialog fills an empty slot of a group appointment. The
        // id is all it needs: it loads the appointment itself and resolves the
        // group (title, slot list, "New appointment" shortcut context) from it.
        openAddPersonDialog(appointmentId) {
            this.liftPopupZIndex();
            this.pendingAddPersonContext = `${ENQUIRY_EDIT_APPOINTMENT_CONTEXT_PREFIX}-addperson-${Date.now()}`;
            this.addPersonChild = { Id: appointmentId };
            this.visibleAddPerson = true;
        },
        handleMessage(event) {
            if (event.origin !== ENQUIRY_CENTRE_ORIGIN) return;
            const iframe = this.$refs.enquiryCentreIframe;
            if (event.source !== (iframe && iframe.contentWindow)) return;

            const data = event && event.data;
            const type = data && data.type;
            if (!type) return;

            switch (type) {
                case ENQUIRY_EVENT.CREATE_TASK: {
                    const { props, title } = resolveCreateTaskDrawer(taskService, data.data);
                    this.openTaskDrawer(props, title);
                    break;
                }
                case ENQUIRY_EVENT.CREATE_APPOINTMENT: {
                    const taskId = data.data && data.data.taskId;
                    if (!taskId) return;
                    postMessageForWebview(WINAPP_METHOD.AGENDA.NEW_APPOINTMENT, { TaskId: Number(taskId) }, 'EnquiryCentreWebview');
                    break;
                }
                case ENQUIRY_EVENT.OPEN_PERSON_DETAIL: {
                    const personId = data.data && data.data.personId;
                    if (!personId) return;
                    this.openPersonDetailDrawer(Number(personId));
                    break;
                }
                case ENQUIRY_EVENT.OPEN_PROPERTY_DETAIL: {
                    const propertyId = data.data && data.data.propertyId;
                    if (!propertyId) return;
                    this.openPropertyDetailDrawer(Number(propertyId));
                    break;
                }
                case ENQUIRY_EVENT.APPOINTMENT_CONFIRMATION: {
                    const appointmentId = data.data && data.data.appointmentId;
                    if (!appointmentId) return;
                    this.showAppointmentConfirmation(Number(appointmentId));
                    break;
                }
                case ENQUIRY_EVENT.ADD_TO_GROUP_APPOINTMENT: {
                    const payload = data.data || {};
                    if (!payload.userId || !payload.startTime || !payload.propertyId) return;
                    this.openAddToGroupAppointmentDrawer({
                        userId: Number(payload.userId),
                        startTime: String(payload.startTime),
                        propertyId: Number(payload.propertyId),
                    });
                    break;
                }
                case ENQUIRY_EVENT.PRINT_APPOINTMENT: {
                    // Not supported here: the print-template modal lives in the app
                    // header, which the webview layout renders with v-show=false.
                    console.warn('print-appointment is not supported in the WinApp Enquiry webview');
                    break;
                }
                case ENQUIRY_EVENT.ADD_APPOINTMENT_COMMENT: {
                    const payload = data.data || {};
                    if (!payload.appointmentId || !payload.startTime || !payload.userId || !payload.propertyId) return;
                    this.openAddCommentDialog({
                        appointmentId: Number(payload.appointmentId),
                        startTime: String(payload.startTime),
                        userId: Number(payload.userId),
                        propertyId: Number(payload.propertyId),
                        personId: payload.personId ? Number(payload.personId) : null,
                        appointmentTypeId: payload.appointmentTypeId ? Number(payload.appointmentTypeId) : null,
                        personName: payload.personName,
                        propertyName: payload.propertyName,
                    });
                    break;
                }
                case ENQUIRY_EVENT.CREATE_DEMAND: {
                    const payload = data.data || {};
                    if (!payload.propertyId || !payload.personId) return;
                    this.openCreateDemandDrawer({ propertyId: Number(payload.propertyId), personId: Number(payload.personId) });
                    break;
                }
                case ENQUIRY_EVENT.EDIT_APPOINTMENT: {
                    const appointmentId = data.data && data.data.appointmentId;
                    if (!appointmentId) return;
                    this.openEditAppointmentDrawer(Number(appointmentId));
                    break;
                }
                case ENQUIRY_EVENT.DELETE_APPOINTMENT: {
                    const appointmentId = data.data && data.data.appointmentId;
                    if (!appointmentId) return;
                    this.deleteAppointment(Number(appointmentId));
                    break;
                }
                case ENQUIRY_EVENT.ADD_PERSON_TO_APPOINTMENT: {
                    const appointmentId = data.data && data.data.appointmentId;
                    if (!appointmentId) return;
                    this.openAddPersonDialog(Number(appointmentId));
                    break;
                }
                case ENQUIRY_EVENT.VIEW_EMAIL: {
                    const historyRootId = data.data && data.data.historyRootId;
                    if (!historyRootId) return;
                    this.openEmailOut(Number(historyRootId));
                    break;
                }
                default:
                    break;
            }
        },
    },
    created() {
        if (!this.isWebview) {
            return;
        }
        // Mark this as an Enquiry webview session so the global back-bar only appears here,
        // not on other webviews (AI image, document, etc.).
        this.$store.commit(`${storeModules.Common}/setEnquiryWebviewSession`, true);
        this.enquiryCentreUrl = this.buildEnquiryCentreUrl();
    },
    mounted() {
        window.addEventListener('message', this.handleMessage);
        this.unregisterAccessToken = embeddedAppTokenBroker.register({
            origin: ENQUIRY_CENTRE_ORIGIN,
            getContentWindow: () => (this.$refs.enquiryCentreIframe && this.$refs.enquiryCentreIframe.contentWindow) || null,
            getLanguageId: () => LanguageHelper.convertLanguageInt(this.$i18n.locale),
        });
        EventBus.$on(EVENT_BUS.AGENDA.CREATE_NEW_TASK, this.onTaskCreatedFromEnquiry);
        EventBus.$on(EVENT_BUS.AGENDA.CREATE_NEW_APPOINTMENT, this.onAppointmentCreatedFromEnquiry);
    },
    beforeDestroy() {
        window.removeEventListener('message', this.handleMessage);
        if (this.unregisterAccessToken) this.unregisterAccessToken();
        EventBus.$off(EVENT_BUS.AGENDA.CREATE_NEW_TASK, this.onTaskCreatedFromEnquiry);
        EventBus.$off(EVENT_BUS.AGENDA.CREATE_NEW_APPOINTMENT, this.onAppointmentCreatedFromEnquiry);
    },
}
</script>
<style scoped>
/* Header row sits above the iframe, which starts below it. */
.enquiry-centre-webview-header {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 48px;
    display: flex;
    align-items: center;
    padding: 0 12px;
    background: #fff;
    border-bottom: 1px solid #e4e7ed;
    box-sizing: border-box;
    /* One above the iframe (9999), below the webview drawer (100000). */
    z-index: 10000;
}
.enquiry-centre-webview-header .el-button {
    display: flex;
    align-items: center;
}
.enquiry-centre-webview-back-icon {
    display: block;
    width: 20px;
    height: 20px;
}
.enquiry-centre-webview-iframe {
    position: fixed;
    top: 48px;
    height: calc(100% - 48px);
    width: 100%;
    left: 0;
    z-index: 9999;
}
</style>
